


































































































































































import Vue from 'vue';
import Component, {mixins} from 'vue-class-component';
import {Action, Getter, State} from 'vuex-class';
import Role from '@/modules/authorization/model/Role';
import AuthorizationItemTree from '@/modules/authorization/components/AuthorizationItemTree.vue';
import {Watch} from 'vue-property-decorator';
import {DateMixin} from '@/modules/core/date';
import {Colors, notify} from '@/modules/core/notification';
import AssignedRoleUserList from '@/modules/authorization/components/AssignedRoleUserList.vue';
import RoleUserAssignation from '@/modules/authorization/model/RoleUserAssignation';
import PermissionSelect from '@/modules/authorization/components/PermissionSelect.vue';
import {CheckAuthorizationResult} from '@/modules/authorization/model/CheckAuthorizationResult';
import {MutationTypes} from '@/modules/authorization/store/mutations';
import Permission from '@/modules/authorization/model/Permission';

/**
 * Detalle de un rol
 *
 * Muestra al usuario una pantalla donde puede modificar la información de un determinado
 * rol además de poder gestionar los permisos asociados al rol
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {PermissionSelect, AssignedRoleUserList, AuthorizationItemTree},
})
export default class RoleDetail extends mixins(DateMixin) {
    public $refs: Vue['$refs'] & {
        tree: {
            selectNode: (node: string, descendants: boolean) => void
            clearSelection: () => void,
        },
        users: {
            reload: () => void,
        },
    }

    /**
     * Rol actualmente en edición el usuario
     *
     * @var { Role }
     */
    @State(state => state.authorization.role)
    public role: Role

    @State(state => state.authorization.permissions)
    public permissions: Permission

    @State(state => state.authorization.editingRole)
    public editing: boolean

    @Getter('authorization/granted')
    public permissionGranted: (path: string) => boolean

    @Action('authorization/checkAuthorization')
    public checkAuthorization: (permissions: string[]) => Promise<CheckAuthorizationResult>

    /**
     * Indica si la aplicación esta guardando el rol actualmente en edición
     * por el usuario
     */
    public saving = false

    public tab = 0
    public updatingAssignation = false

    public loading = false

    public canUpdateRole = true
    public canAssignPermissions = true
    public canAssignUsers = true

    public get cardStyles () {
        return {
            height: `calc(100vh - ${this.$vuetify.breakpoint.mdAndUp ? '9.4' : '5.9'}rem)`,
            overflow: 'auto',
        }
    }

    /**
     * Cambio de rol
     *
     * El rol actual que esta en edición ha cambiado
     */
    @Watch('role')
    public async onRoleChange (role: Role) {
        this.loading = true

        const { updatePermission, userAssignmentPermission, permissionAssignmentPermission } = role
        // Solicita al sistema de autorización los permisos necesarios para editar el rol
        const auth = await this.checkAuthorization([
            updatePermission,
            userAssignmentPermission,
            permissionAssignmentPermission,
        ])

        this.canUpdateRole = updatePermission ? auth.isGranted(updatePermission) : true
        this.canAssignUsers = userAssignmentPermission ? auth.isGranted(userAssignmentPermission) : true
        this.canAssignPermissions = permissionAssignmentPermission
            ? auth.isGranted(permissionAssignmentPermission)
            : true

        // El árbol de permisos se encuentra habilitado para el usuario
        if (this.$refs.tree) {
            this.$refs.tree.clearSelection()
            role.permissions.forEach(p => this.$refs.tree.selectNode(p, true))

            await this.checkAuthorization(this.permissions.assignToRolesPermissionList)
        }


        this.tab = 0
        this.loading = false
    }

    public treeSelectFilter (permission: Permission) {
        if (!permission.assignToRolesPermission) {
            return true
        }

        return this.permissionGranted(permission.assignToRolesPermission)
    }

    /**
     * Indica si la edición del permiso se encuentra deshabilitada
     */
    public get disabled () {
        return !this.editing || this.saving || !this.canUpdateRole
    }

    /**
     * Al crear el componente solicita a la aplicación carga el árbol completo
     * de roles de usuario
     */
    public async created () {
        await this.$store.dispatch('authorization/loadFullPermissionTree')
    }

    /**
     * Actualiza la lista de usuarios asignados al rol que se encuentra actualmente
     * en edición
     */
    public async updateAssignation () {
        const count = this.$store.state.authorization.updatedRoleUserAssignationList.length
        this.updatingAssignation = true
        await this.$store.dispatch('authorization/updateRoleUserAssignationList')
        await this.$refs.users.reload()
        this.updatingAssignation = false

        notify(`La asignación de usuarios del rol ${this.role.path} se ha guardado correctamente`)
    }

    /**
     * Solicita a la aplicación guardar los cambios realizados por el usuario
     * sobre el rol actual
     */
    public async save () {
        this.saving = true
        await this.$store.dispatch('authorization/saveRole')

        this.saving = false
        notify(`El rol ${this.role.title} se ha guardado correctamente`);
    }

    public errorCaptured (err: Error, vm: Vue) {
        notify(err.message, Colors.Error)
        this.saving = false
    }

    public beforeDestroy () {
        this.$store.commit(`authorization/${MutationTypes.ResetRole}`)
        this.$store.commit(`authorization/${MutationTypes.ToggleEditingRole}`, false)
    }
}
